class KCMSFForm {
	constructor(el) {
		this.form = el;
		this.totalSteps = parseInt(el.dataset.totalSteps, 10);
		this.currentStep = 1;
		this.uploadedFiles = {};
		this.activeUploads = 0;
		this.isSubmitting = false;
		this.hasStarted = false;
		this.tracking = null;
		try {
			const t = el.dataset.tracking;
			if (t) this.tracking = JSON.parse(t);
		} catch {}

		this.progressLabel = el.querySelector('.kcmsf-progress__label');
		this.gaugeFill = el.querySelector('.kcmsf-gauge__fill');
		this.dots = el.querySelectorAll('.kcmsf-progress__dot');
		this.confirmation = el.querySelector('.kcmsf-confirmation');
		this.footer = el.querySelector('.kcmsf-footer');
		this.errorEl = el.querySelector('.kcmsf-error');
		this.btnBack = el.querySelector('.kcmsf-btn-back');
		this.btnNext = el.querySelector('.kcmsf-btn-next');
		this.btnLabel = el.querySelector('.kcmsf-btn-next__label');
		this.btnPct = el.querySelector('.kcmsf-btn-next__pct');

		this.btnBack.addEventListener('click', () => this.back());
		this.btnNext.addEventListener('click', () => this.advance());

		const onFormInput = () => {
			this.updateGauge();
			this.evaluateConditionals();
		};
		el.addEventListener('input', onFormInput);
		el.addEventListener('change', onFormInput);

		this.setupDropzones();
		this.setupCardChoices();
		this.showStep(1);
		this.evaluateConditionals();
		this.setupStartTracking();
	}

	// ── Step navigation ───────────────────────────────────────────────────────

	showStep(n) {
		this.currentStep = n;

		this.form.querySelectorAll('.kcmsf-step').forEach((step) => {
			const active = parseInt(step.dataset.step, 10) === n;
			step.classList.toggle('is-active', active);
		});

		this.dots.forEach((dot, i) => {
			dot.classList.toggle('is-active', i + 1 === n);
			dot.classList.toggle('is-done', i + 1 < n);
		});

		if (this.progressLabel) this.progressLabel.textContent = `ÉTAPE ${n} SUR ${this.totalSteps}`;

		this.btnBack.hidden = n === 1;

		if (this.btnLabel) this.btnLabel.textContent = n === this.totalSteps ? 'Envoyer →' : 'Continuer →';

		this.updateGauge();
		this.clearError();
		this.getStepEl(n)?.querySelector('input, textarea, select')?.focus();
	}

	getStepEl(n) {
		return this.form.querySelector(`.kcmsf-step[data-step="${n}"]`);
	}

	advance() {
		if (this.isSubmitting) return;
		const error = this.validateStep(this.currentStep);
		if (error) {
			this.showError(error);
			return;
		}

		const fromStep = this.currentStep;
		if (fromStep < this.totalSteps) {
			this.showStep(fromStep + 1);
			this.pushStepEvent(fromStep);
		} else {
			this.submitForm();
		}
	}

	back() {
		if (this.currentStep > 1) this.showStep(this.currentStep - 1);
	}

	// ── Progress gauge ────────────────────────────────────────────────────────

	updateGauge() {
		const stepEl = this.getStepEl(this.currentStep);
		const wrappers = new Set();
		const filled = new Set();

		stepEl?.querySelectorAll('input:not([type="hidden"]), textarea, select').forEach((inp) => {
			if (inp.type === 'file') return;
			const wrapper = inp.closest('.kcmsf-field, .kcmsf-dropzone');
			if (!wrapper || getComputedStyle(wrapper).display === 'none') return;
			wrappers.add(wrapper);
			if (inp.type === 'checkbox' || inp.type === 'radio') {
				if (inp.checked) filled.add(wrapper);
			} else if (inp.value.trim()) {
				filled.add(wrapper);
			}
		});

		const total = wrappers.size || 1;
		const base = (this.currentStep - 1) / this.totalSteps;
		const stepContrib = filled.size / total / this.totalSteps;
		const pct = Math.round(Math.min(100, (base + stepContrib) * 100));

		if (this.gaugeFill) this.gaugeFill.style.width = `${pct}%`;
		if (this.btnPct) this.btnPct.textContent = `${pct}\u00a0%`;
		if (this.btnNext) this.btnNext.style.setProperty('--kcmsf-btn-progress', `${pct}%`);
	}

	// ── Validation ────────────────────────────────────────────────────────────

	validateStep(n) {
		const stepEl = this.getStepEl(n);

		for (const wrapper of stepEl.querySelectorAll('[data-required="1"]')) {
			if (getComputedStyle(wrapper).display === 'none') continue;

			// File dropzone
			if (wrapper.classList.contains('kcmsf-dropzone')) {
				const fieldName = wrapper.dataset.fieldName;
				if (!this.uploadedFiles[fieldName]?.length) {
					const label = wrapper.closest('.kcmsf-field')?.querySelector('.kcmsf-field__label');
					return `"${label?.textContent.replace('*', '').trim() ?? 'Ce fichier'}" est requis.`;
				}
				continue;
			}

			const inputs = wrapper.querySelectorAll('input, textarea, select');
			let hasValue = false;

			inputs.forEach((inp) => {
				if (inp.type === 'checkbox' || inp.type === 'radio') {
					if (inp.checked) hasValue = true;
				} else if (inp.value.trim()) hasValue = true;
			});

			if (!hasValue) {
				const labelEl = wrapper.querySelector('.kcmsf-field__label, .kcmsf-field__consent-text');
				const text = labelEl?.textContent.replace('*', '').trim() ?? 'Ce champ';
				wrapper.classList.add('has-error');
				return `"${text}" est requis.`;
			}
			wrapper.classList.remove('has-error');
		}

		return null;
	}

	// ── Conditional logic ─────────────────────────────────────────────────────

	evaluateConditionals() {
		this.form.querySelectorAll('[data-conditional]').forEach((wrapper) => {
			let logic;
			try {
				logic = JSON.parse(wrapper.dataset.conditional);
			} catch {
				return;
			}

			const visible = this.evaluateLogic(logic);
			wrapper.style.display = visible ? '' : 'none';
			if (!visible) {
				wrapper.querySelectorAll('input, select, textarea').forEach((inp) => {
					if (inp.type === 'checkbox' || inp.type === 'radio') inp.checked = false;
					else inp.value = '';
				});
			}
		});
	}

	evaluateLogic(logic) {
		// ACF format: outer = OR groups, inner = AND conditions
		if (!Array.isArray(logic) || !logic.length) return true;
		return logic.some((andGroup) => andGroup.every((rule) => this.evaluateRule(rule)));
	}

	evaluateRule(rule) {
		const input = this.form.querySelector(`[data-key="${CSS.escape(rule.field)}"]`);
		if (!input) return true;

		let current;
		if (input.type === 'checkbox' || input.type === 'radio') {
			const checked = [...this.form.querySelectorAll(`[name="${CSS.escape(input.name)}"]:checked`)];
			current = checked.map((i) => i.value);
		} else {
			current = input.value;
		}

		switch (rule.operator) {
			case '==':
				return Array.isArray(current) ? current.includes(rule.value) : current === rule.value;
			case '!=':
				return Array.isArray(current) ? !current.includes(rule.value) : current !== rule.value;
			case '>':
				return parseFloat(current) > parseFloat(rule.value);
			case '<':
				return parseFloat(current) < parseFloat(rule.value);
			case '==empty':
				return !current || !current.length;
			case '!=empty':
				return !!(current && current.length);
			case '==contains':
				return typeof current === 'string' && current.includes(rule.value);
			default:
				return true;
		}
	}

	// ── Card-grid choices ─────────────────────────────────────────────────────

	setupCardChoices() {
		this.form.querySelectorAll('.kcmsf-choice--card').forEach((card) => {
			const input = card.querySelector('.kcmsf-choice__input');
			if (!input) return;
			input.addEventListener('change', () => this.syncCardStates(input.name));
		});
	}

	syncCardStates(inputName) {
		const escapedName = CSS.escape(inputName);
		this.form.querySelectorAll(`[name="${escapedName}"], [name="${escapedName}[]"]`).forEach((inp) => {
			inp.closest('.kcmsf-choice--card')?.classList.toggle('is-selected', inp.checked);
		});
	}

	// ── File dropzones ────────────────────────────────────────────────────────

	setupDropzones() {
		this.form.querySelectorAll('.kcmsf-dropzone').forEach((zone) => {
			const fieldName = zone.dataset.fieldName;
			const maxFiles = parseInt(zone.dataset.maxFiles, 10) || 5;
			const fileInput = zone.querySelector('.kcmsf-dropzone__input');
			const counter = zone.querySelector('.kcmsf-dropzone__counter');
			const preview = zone.querySelector('.kcmsf-dropzone__preview');
			const idsInput = zone.querySelector('.kcmsf-dropzone__ids');

			if (!this.uploadedFiles[fieldName]) this.uploadedFiles[fieldName] = [];

			const updateCounter = () => {
				const count = this.uploadedFiles[fieldName].length;
				if (counter) counter.textContent = `${count} / ${maxFiles}`;
			};

			const processFiles = async (files) => {
				for (const file of Array.from(files)) {
					if (this.uploadedFiles[fieldName].length >= maxFiles) break;
					await this.uploadFile(file, fieldName, preview, idsInput);
					updateCounter();
				}
			};

			zone.addEventListener('click', (e) => {
				if (e.target.closest('.kcmsf-dropzone__preview')) return;
				fileInput.click();
			});

			fileInput.addEventListener('change', () => {
				processFiles(fileInput.files);
				fileInput.value = '';
			});

			zone.addEventListener('dragover', (e) => {
				e.preventDefault();
				zone.classList.add('is-dragging');
			});
			zone.addEventListener('dragleave', (e) => {
				if (!zone.contains(e.relatedTarget)) zone.classList.remove('is-dragging');
			});
			zone.addEventListener('drop', (e) => {
				e.preventDefault();
				zone.classList.remove('is-dragging');
				processFiles(e.dataTransfer.files);
			});
		});
	}

	async uploadFile(file, fieldName, previewEl, idsInput) {
		const thumb = document.createElement('div');
		thumb.className = 'kcmsf-dropzone__thumb is-uploading';
		thumb.textContent = file.name;
		previewEl?.appendChild(thumb);

		const body = new FormData();
		body.append('action', 'kcmsf_upload_file');
		body.append('nonce', kcmsf.fileNonce);
		body.append('file', file);

		try {
			const res = await fetch(kcmsf.ajaxUrl, { method: 'POST', body });
			const json = await res.json();

			if (!json.success) {
				thumb.classList.replace('is-uploading', 'has-error');
				thumb.title = json.data?.message ?? 'Upload failed';
				return;
			}

			this.uploadedFiles[fieldName].push(json.data.id);
			if (idsInput) idsInput.value = this.uploadedFiles[fieldName].join(',');

			thumb.classList.replace('is-uploading', 'is-done');

			if (/\.(jpe?g|gif|png|webp)$/i.test(json.data.url ?? '')) {
				const img = document.createElement('img');
				img.src = json.data.url;
				img.alt = file.name;
				thumb.textContent = '';
				thumb.appendChild(img);
			}
		} catch {
			thumb.classList.replace('is-uploading', 'has-error');
		}
	}

	// ── Submission ────────────────────────────────────────────────────────────

	async submitForm() {
		if (this.activeUploads > 0) {
			this.showError('Veuillez attendre la fin du téléchargement des fichiers.');
			return;
		}
		this.isSubmitting = true;
		this.btnNext.disabled = true;
		this.btnNext.classList.add('is-loading');
		this.clearError();

		const body = new FormData(this.form);
		body.set('action', 'kcmsf_submit');

		try {
			const res = await fetch(kcmsf.ajaxUrl, { method: 'POST', body });
			const json = await res.json();

			if (!json.success) {
				this.showError(json.data?.message ?? 'Une erreur est survenue.');
				this.isSubmitting = false;
				this.btnNext.disabled = false;
				this.btnNext.classList.remove('is-loading');
				return;
			}

			this.form.querySelectorAll('.kcmsf-step').forEach((s) => s.classList.remove('is-active'));
			this.form.querySelector('.kcmsf-progress')?.setAttribute('hidden', '');
			this.form.querySelector('.kcmsf-gauge')?.setAttribute('hidden', '');
			this.footer?.setAttribute('hidden', '');
			this.pushStepEvent(this.totalSteps);
			this.pushSubmitEvent();
			if (this.confirmation) {
				this.confirmation.removeAttribute('hidden');
				this.confirmation.hidden = false;
			}
		} catch {
			this.showError('Erreur réseau. Veuillez réessayer.');
			this.isSubmitting = false;
			this.btnNext.disabled = false;
			this.btnNext.classList.remove('is-loading');
		}
	}

	// ── Tracking ─────────────────────────────────────────────────────────────────

	setupStartTracking() {
		if (!this.tracking) return;
		const step1 = this.getStepEl(1);
		if (!step1) return;
		const trigger = () => {
			if (this.hasStarted) return;
			this.hasStarted = true;
			this.pushDataLayer('form_funnel_start');
		};
		step1.addEventListener('focusin', trigger);
		step1.addEventListener('pointerdown', trigger);
	}

	pushDataLayer(event, extra = {}) {
		if (!window.dataLayer || !this.tracking) return;
		window.dataLayer.push({
			event,
			form_name: this.tracking.formName,
			page_type: this.tracking.pageType,
			page_url: location.pathname,
			...extra
		});
	}

	pushStepEvent(stepIndex) {
		if (!this.tracking) return;
		const stepData = this.tracking.steps?.[stepIndex - 1];
		this.pushDataLayer(`form_funnel_step_${stepIndex}`, {
			step_number: stepIndex,
			step_name: stepData?.name ?? `step_${stepIndex}`
		});
	}

	pushSubmitEvent() {
		if (!this.tracking) return;

		let profil = '';
		if (this.tracking.profilField) {
			const inp = this.form.querySelector(`input[name="fields[${this.tracking.profilField}]"]:checked`);
			profil = inp?.value ?? '';
		}

		const projetValues = [];
		for (const fieldName of this.tracking.projetFields ?? []) {
			const wrapper = this.form.querySelector(`input[name="fields[${fieldName}][]"]`)?.closest('.kcmsf-field');
			if (wrapper && getComputedStyle(wrapper).display === 'none') continue;
			this.form.querySelectorAll(`input[name="fields[${fieldName}][]"]:checked`).forEach((inp) => {
				projetValues.push(inp.value);
			});
		}

		const nbFichiers = Object.values(this.uploadedFiles).reduce((sum, ids) => sum + ids.length, 0);

		const extra = { profil, projet_type: projetValues.join(','), nb_fichiers: nbFichiers };

		if (this.tracking.budgetField) {
			const budgetInp = this.form.querySelector(
				`input[name="fields[${this.tracking.budgetField}]"], textarea[name="fields[${this.tracking.budgetField}]"]`
			);
			extra.budget_renseigne = !!budgetInp?.value?.trim();
		}

		this.pushDataLayer(`form_funnel_submit_${this.tracking.formName}_form`, extra);
	}

	// ── Helpers ───────────────────────────────────────────────────────────────

	showError(msg) {
		if (!this.errorEl) return;
		this.errorEl.textContent = msg;
		this.errorEl.hidden = false;
	}

	clearError() {
		if (!this.errorEl) return;
		this.errorEl.textContent = '';
		this.errorEl.hidden = true;
	}
}

document.querySelectorAll('[data-kcmsf-form]').forEach((el) => new KCMSFForm(el));
